import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatPrice, INTRO_MONTHS, INTRO_PERCENT_OFF, introMonthly, monthlyPrice, PLAN_DETAILS, PLANS, planById, type Currency, type PriceId } from '@mybooks/plans';
import { api, ApiError, unwrap, type Schemas } from '../../api/client';
import { useSession } from '../../auth/session';
import { clearCheckout } from '../../auth/checkout';
import { fmtDate } from '../../lib/format';
import { errorText, Icon, Loading } from '../../ui/kit';
import { Logo } from '../../ui/Logo';
import './billing.css';
import { APP_NAME } from '../../lib/brand';

// Subscriptions (ADR 0033). A plan belongs to the person, not to a business, so nothing here is
// keyed by the business in use; signing out clears the cache.

export type Billing = Schemas['Billing'];
export type Subscription = Schemas['Subscription'];

export const BILLING_KEY = ['me', 'billing'] as const;
export const useBilling = () => useQuery({ queryKey: BILLING_KEY, queryFn: async () => (await unwrap(api.GET('/v1/billing'))).data as Billing });

const STATUS: Record<Subscription['status'] & string, { label: string; pill: string }> = {
  active: { label: 'Active', pill: 'paid' },
  trialing: { label: 'Trial', pill: 'paid' },
  past_due: { label: 'Payment failed', pill: 'overdue' },
  unpaid: { label: 'Unpaid', pill: 'overdue' },
  incomplete: { label: 'Waiting for payment', pill: 'pending' },
  incomplete_expired: { label: 'Not paid', pill: 'void' },
  canceled: { label: 'Cancelled', pill: 'void' },
  paused: { label: 'Paused', pill: 'pending' },
};
export const inForce = (s: Subscription | null | undefined) => !!s && (s.status === 'active' || s.status === 'trialing' || s.status === 'past_due');
const planName = (id: string | undefined) => planById(id ?? '')?.name ?? id ?? '';
export const companiesText = (limit: number | null | undefined) => (limit == null ? 'Unlimited companies' : `${limit} ${limit === 1 ? 'company' : 'companies'}`);

/** Sends the browser to Stripe: Checkout for a new plan, or the customer portal for an existing one. */
async function goTo(call: Promise<{ data?: { data?: { url?: string } }; error?: unknown; response: Response }>) {
  const url = (await unwrap(call)).data?.url;
  if (url) window.location.assign(url);
}

/**
 * The three plans as the website's /pricing shows them (apps/site PricingPlans): monthly or yearly,
 * in the visitor's currency, with the intro offer on monthly plans for a first subscription, and
 * Growth on a grey panel as the most popular. Each starts a Stripe Checkout for the signed-in person.
 */
export function PlanPicker({ current }: { current?: string | null }) {
  const billing = useBilling();
  const [yearly, setYearly] = useState(false);
  const [busy, setBusy] = useState<PriceId | null>(null);
  const [error, setError] = useState('');
  const currency: Currency = billing.data?.currency ?? 'usd';
  const intro = billing.data?.intro_offer ?? true;
  const money = (n: number, cents = false) => formatPrice(n, currency, cents);
  const choose = async (plan: PriceId) => {
    setBusy(plan); setError('');
    try { await goTo(api.POST('/v1/billing/checkout', { body: { plan } })); } catch (e) { setError(errorText(e)); setBusy(null); }
  };
  if (billing.isLoading) return <Loading />;
  return (
    <div className="plan-picker">
      <div className="plan-toggle" role="group" aria-label="Billing">
        <button type="button" aria-pressed={!yearly} onClick={() => setYearly(false)}>Monthly</button>
        <button type="button" aria-pressed={yearly} onClick={() => setYearly(true)}>Yearly, 2 months free</button>
      </div>
      <div className="plan-grid">
        {PLANS.map((p) => {
          const id = `${p.id}_${yearly ? 'yearly' : 'monthly'}` as PriceId;
          const details = PLAN_DETAILS[p.id];
          const price = monthlyPrice(p.id, currency);
          return (
            <div key={p.id} className={`plan-option${details.popular ? ' popular' : ''}${current === p.id ? ' current' : ''}`}>
              {details.popular && <span className="plan-badge">Most popular</span>}
              <h3>{p.name}</h3>
              <p className="plan-scope">{details.scope}</p>
              {yearly ? (
                <>
                  <p className="plan-price"><strong>{money(price * 10)}</strong><span>/yr</span></p>
                  <p className="plan-terms">Works out at {money((price * 10) / 12, true)}/mo.</p>
                </>
              ) : intro ? (
                <>
                  <p className="plan-price"><s>{money(price)}</s><strong>{money(introMonthly(price), true)}</strong><span>/mo</span></p>
                  <p className="plan-terms">{INTRO_PERCENT_OFF}% off for {INTRO_MONTHS} months, then {money(price)}/mo.</p>
                </>
              ) : (
                <>
                  <p className="plan-price"><strong>{money(price)}</strong><span>/mo</span></p>
                  <p className="plan-terms">Billed monthly. Cancel anytime.</p>
                </>
              )}
              <button type="button" className="plan-choose" disabled={busy !== null} onClick={() => void choose(id)}>{busy === id ? 'Opening Stripe…' : `Choose ${p.name}`}</button>
              <ul className="plan-features">
                {details.features.map((f) => <li key={f}><Icon name="check" size={15} />{f}</li>)}
              </ul>
            </div>
          );
        })}
      </div>
      <p className="muted plan-note">You pay securely with Stripe.{intro ? ` The intro offer is for a first subscription on a monthly plan.` : ''}</p>
      {error && <div className="notice bad" role="alert" style={{ margin: 0 }}><Icon name="alert" size={17} /><div>{error}</div></div>}
    </div>
  );
}

/** Settings → Subscription: the person's plan, the companies it allows, and Stripe's portal. */
export function SubscriptionSettings() {
  const billing = useBilling();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (billing.isLoading) return <Loading />;
  if (billing.isError || !billing.data) return <div className="notice bad" role="alert"><Icon name="alert" size={17} /><div>{errorText(billing.error)}</div></div>;
  const { subscription: sub, company_limit: limit, companies_owned: owned, configured } = billing.data;
  const active = inForce(sub);
  const manage = async () => {
    setBusy(true); setError('');
    try { await goTo(api.POST('/v1/billing/portal')); } catch (e) { setError(errorText(e)); setBusy(false); }
  };
  const status = sub?.status ? STATUS[sub.status] : null;

  return (
    <>
      <div className="card form-card">
        <div className="form-head"><h2>Your plan</h2><p>Your plan covers every company you own, with unlimited users in each. It is yours, not the company's: the companies you are invited to are covered by their owner's plan.</p></div>
        {sub ? (
          <dl className="plan-summary">
            <dt>Plan</dt>
            <dd><strong>{planName(sub.plan)}</strong>, billed {sub.billing_interval === 'year' ? 'yearly' : 'monthly'} {status && <span className={`pill status ${status.pill}`}>{status.label}</span>} {sub.livemode === false && <span className="pill">Test mode</span>}</dd>
            {sub.current_period_end && <><dt>{sub.cancel_at_period_end || !active ? 'Ends' : 'Renews'}</dt><dd>{fmtDate(sub.current_period_end)}</dd></>}
            <dt>Companies</dt>
            <dd>{limit == null ? `${owned}, no limit` : `${owned} of ${limit} used`}</dd>
          </dl>
        ) : (
          <p className="muted" style={{ margin: 0 }}>
            You have no plan. Choose one to add companies and keep using the ones you own.
          </p>
        )}
        {sub?.status === 'past_due' && <div className="notice bad" style={{ margin: 0 }}><Icon name="alert" size={17} /><div>Your last payment failed. Stripe will try again; update your card under Manage billing to keep your plan.</div></div>}
        {sub && sub.cancel_at_period_end && active && <div className="notice" style={{ margin: 0 }}><Icon name="alert" size={17} /><div>Your plan is cancelled and ends on {fmtDate(sub.current_period_end)}. You keep your companies, but cannot add one after that.</div></div>}
        {error && <div className="notice bad" role="alert" style={{ margin: 0 }}><Icon name="alert" size={17} /><div>{error}</div></div>}
        {sub && configured && (
          <div className="form-foot"><span className="muted" style={{ fontSize: 12.5 }}>Change plan, update your card, see invoices or cancel at Stripe.</span><span className="spacer" /><button type="button" className="btn primary" disabled={busy} onClick={() => void manage()}>{busy ? 'Opening Stripe…' : 'Manage billing'}</button></div>
        )}
      </div>
      {!active && (
        <div className="card form-card">
          <div className="form-head"><h2>{sub ? 'Choose a plan again' : 'Choose a plan'}</h2><p>Every plan has every feature and unlimited users. Plans differ in how many companies you can own.</p></div>
          {configured ? <PlanPicker current={sub?.plan ?? null} /> : <p className="muted" style={{ margin: 0 }}>Payments are not set up here yet.</p>}
        </div>
      )}
    </>
  );
}

/** Shown in place of an error when a new business would go over the plan. */
export function PlanLimitNotice({ message, inShell = true }: { message: string; inShell?: boolean }) {
  return (
    <div className="notice" role="alert" style={{ margin: 0 }}>
      <Icon name="alert" size={17} />
      <div>{message}. {inShell ? <a href="/account?tab=billing">See your plan</a> : 'Choose a plan below.'}</div>
    </div>
  );
}

export const isPlanLimit = (e: unknown) => e instanceof ApiError && e.code === 'plan_limit';

type LinkState =
  | { step: 'linking' }
  | { step: 'linked'; subscription: Subscription | null }
  | { step: 'mismatch'; checkoutEmail: string | null; accountEmail: string }
  | { step: 'failed'; message: string };

/**
 * After paying, the buyer lands here signed in. The subscription is added to their account; if the
 * email they gave Stripe is not the one they are signed in with, they choose first.
 */
export function LinkCheckout({ id, onDone }: { id: string; onDone: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [state, setState] = useState<LinkState>({ step: 'linking' });

  const link = async (confirm: boolean) => {
    setState({ step: 'linking' });
    try {
      const result = (await unwrap(api.POST('/v1/billing/checkout-sessions/{id}/link', { params: { path: { id } }, body: { confirm } }))).data as Schemas['CheckoutLink'];
      if (result.status === 'email_mismatch') {
        setState({ step: 'mismatch', checkoutEmail: result.checkout_email ?? null, accountEmail: result.account_email ?? session.user?.email ?? '' });
        return;
      }
      void queryClient.invalidateQueries({ queryKey: BILLING_KEY });
      setState({ step: 'linked', subscription: result.subscription ?? null });
    } catch (e) {
      setState({ step: 'failed', message: e instanceof ApiError && e.code === 'subscription_taken' ? `This subscription already belongs to another ${APP_NAME} account. Sign in with that account to use it.` : errorText(e) });
    }
  };
  // Link once per checkout; `link` only reads the id and stable setters.
  useEffect(() => { void link(false); }, [id]);

  // Reload the businesses too: a plan in force opens the ones it pays for (ADR 0040).
  const finish = () => { clearCheckout(); void session.refresh().finally(onDone); };
  const frame = (title: string, sub: string, body: React.ReactNode) => (
    <div className="auth-page"><div className="card auth-card"><Logo size={44} /><h1>{title}</h1><p className="muted">{sub}</p>{body}</div></div>
  );

  switch (state.step) {
    case 'linking':
      return <div className="auth-page"><Loading label="Adding your subscription" /></div>;
    case 'linked': {
      const p = planById(state.subscription?.plan ?? '');
      return frame('Your plan is active', p ? `${p.name}: ${companiesText(p.companyLimit)}, unlimited users. Thank you for subscribing.` : 'Thank you for subscribing.',
        <div className="auth-form"><button className="btn primary" onClick={finish}>Continue</button></div>);
    }
    case 'mismatch':
      return frame('Which account is this for?', `You paid as ${state.checkoutEmail ?? 'another email'}, but you are signed in as ${state.accountEmail}.`,
        <div className="auth-form">
          <button className="btn primary" onClick={() => void link(true)}>Add the plan to {state.accountEmail}</button>
          <button className="btn" onClick={() => { void session.signOut(); }}>Sign out and continue as {state.checkoutEmail ?? 'the buyer'}</button>
          <button className="link-btn" onClick={finish}>Not now</button>
        </div>);
    case 'failed':
      return frame('We could not add your plan', state.message,
        <div className="auth-form"><button className="btn" onClick={() => void link(false)}>Try again</button><button className="link-btn" onClick={finish}>Continue without it</button></div>);
  }
}
